import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../ui/button'
import { Separator } from '../../ui/separator'

interface MultiSelectActionBarProps {
  selectedCount: number
  onClear: () => void
  children: ReactNode
}

/**
 * Floating action bar shown when nodes are multi-selected on a graph.
 * Pure presentational shell — callers provide their own action buttons via `children`.
 */
export function MultiSelectActionBar({
  selectedCount,
  onClear,
  children,
}: MultiSelectActionBarProps) {
  const { t } = useTranslation()
  if (selectedCount === 0) return null

  return (
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30 bg-popover/95 border border-orange-500/50 rounded-lg px-4 py-2 flex items-center gap-3 shadow-lg max-md:bottom-12 max-md:left-3 max-md:right-3 max-md:translate-x-0 max-md:flex-wrap max-md:justify-center max-md:gap-x-2 max-md:gap-y-1 max-md:px-3">
      <span className="text-sm text-orange-300">
        {t('investigations.graph.multiSelect.selected', { count: selectedCount })}
      </span>
      <Separator orientation="vertical" className="h-6 bg-orange-500/30" />
      {children}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground max-md:h-9"
        onClick={onClear}
      >
        <X className="h-3 w-3 mr-1" />
        {t('investigations.graph.multiSelect.clear')}
      </Button>
    </div>
  )
}
